import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Badge from './Badge';
import LinkButton from './LinkButton';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/* 이동 정보 edit section - used inside Card in BookingSettings
   departure: { label?, address, time }
   arrival:   { label, address, icon }

   Route modes (controlled by which props are passed):
   1. Free stopovers: pass onAddStopover, stopovers, etc.
   2. Fixed waypoints (golf/ski/hospital): pass fixedWaypoints array
   3. Both 1+2: golf/ski는 fixedWaypoints(골프장/스키장) + 추가 경유지 모두 가능 (#56/#91)
   4. Simple 2-point: omit both */
export default function RouteEditSection({
  directionLabel,
  departure,
  arrival,
  stopovers = [],
  onAddStopover,
  onRemoveStopover,
  stopoversBefore = [],
  onAddStopoverBefore,
  onRemoveStopoverBefore,
  onEditStopoverBefore,
  notes = '',
  onNotesChange,
  departureDetail = '',
  onDepartureDetailChange,
  arrivalDetail = '',
  onArrivalDetailChange,
  stopoverCharge,
  onEditDeparture,
  onEditArrival,
  onEditStopover,
  fixedWaypoints,
}) {
  const { t } = useTranslation();
  // #56/#91: golf/ski에서도 fixedWaypoints + 추가 경유지 둘 다 표시되도록 fixedWaypoints 조건 제거
  const showFreeStopovers = !!onAddStopover;
  // fixedWaypoints 앞쪽에도 경유지 추가 허용 (hospital 왕복 등 — 출발지→병원 사이)
  const showFreeStopoversBefore = !!onAddStopoverBefore && !!fixedWaypoints;

  // 이동 정보 모든 한국어 필드를 한 번에 DeepL 번역
  const safe = (v) => (typeof v === 'string' ? v : '');
  const wpBundle = useMemo(() => {
    const out = [
      safe(directionLabel),
      safe(departure?.label), safe(departure?.name), safe(departure?.address),
      safe(arrival?.label), safe(arrival?.name), safe(arrival?.address),
    ];
    (fixedWaypoints || []).forEach((wp) => {
      out.push(safe(wp.label), safe(wp.address), safe(wp.detail));
    });
    (stopoversBefore || []).forEach((sp) => {
      // BugHerd #82: 경유지 카드도 도착지처럼 주소 + 장소명(name) 두 줄 노출 위해 name 도 번역 번들에 포함.
      out.push(safe(sp.address), safe(sp.name));
    });
    (stopovers || []).forEach((sp) => {
      out.push(safe(sp.address), safe(sp.name));
    });
    return out;
  }, [directionLabel, departure, arrival, fixedWaypoints, stopoversBefore, stopovers]);
  const tBundle = useDeepLTranslated(wpBundle);
  let idx = 0;
  const tDir = tBundle[idx++] || directionLabel;
  const tDepLabel = tBundle[idx++] || departure?.label;
  const tDepName = tBundle[idx++] || departure?.name;
  const tDepAddr = tBundle[idx++] || departure?.address;
  const tArrLabel = tBundle[idx++] || arrival?.label;
  const tArrName = tBundle[idx++] || arrival?.name;
  const tArrAddr = tBundle[idx++] || arrival?.address;
  const tFixedWaypoints = (fixedWaypoints || []).map((wp) => ({
    ...wp,
    label: tBundle[idx++] || wp.label,
    address: tBundle[idx++] || wp.address,
    detail: tBundle[idx++] || wp.detail,
  }));
  const tStopoversBefore = (stopoversBefore || []).map((sp) => ({
    ...sp,
    address: tBundle[idx++] || sp.address,
    name: tBundle[idx++] || sp.name,
  }));
  const tStopovers = (stopovers || []).map((sp) => ({
    ...sp,
    address: tBundle[idx++] || sp.address,
    name: tBundle[idx++] || sp.name,
  }));

  return (
    <>
      <h3 className="text-lg font-bold text-mono-900">{t('common.route_info_title')}</h3>
      <Badge variant="direction">{tDir}</Badge>

      <div className="flex flex-col">
        {/* ---- Departure ---- */}
        <div className="flex items-center gap-3 sm:gap-5">
          <Icon name={departure.icon || 'van'} size={32} className="text-mono-600 shrink-0" />
          <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-2.5 flex flex-col gap-2.5 min-h-16">
            <div className="flex items-center gap-2.5">
              <div className="flex-1 flex flex-col gap-1">
                <span className="text-[15px] font-medium text-mono-600">{tDepLabel || t('common.departure_label')}</span>
                {/* BugHerd #82 follow-up: 장소명(name) 이 있으면 굵게, 주소(address) 가 보조.
                   name 이 없으면 address 단독으로 굵게 표시 (fallback). */}
                {departure.time && (
                  <span className="text-2sm font-medium text-mono-600">{departure.time.replace('T', ' ')}</span>
                )}
                {tDepName && tDepName !== tDepAddr ? (
                  <>
                    <span className="text-[16px] font-bold text-mono-900">{tDepName}</span>
                    <span className="text-[15px] font-medium text-mono-600">{tDepAddr}</span>
                  </>
                ) : (
                  <span className="text-[16px] font-bold text-mono-900">{tDepAddr}</span>
                )}
              </div>
              <LinkButton size="xs" onClick={onEditDeparture} className="shrink-0 self-start">{t('common.edit')}</LinkButton>
            </div>
            {onDepartureDetailChange && (
              <input
                type="text"
                name="departure-detail"
                autoComplete="off"
                placeholder={t('common.address_detail_placeholder')}
                value={departureDetail}
                onChange={onDepartureDetailChange}
                className="w-full px-3 py-2 bg-white border border-mono-100 rounded-lg text-[15px] text-mono-900 placeholder:text-mono-500 focus:outline-none focus:border-orange-500 transition"
              />
            )}
          </div>
        </div>

        {/* ---- Free stopovers BEFORE fixed waypoint (예: 출발지 → 경유지 → 병원) ---- */}
        {showFreeStopoversBefore && (
          <>
            {/* Add button before first beforeStopover */}
            <div className="flex gap-3 sm:gap-5 py-6">
              <div className="w-8 flex justify-center shrink-0">
                <div className="border-l border-dashed border-text-muted border-gray-400" />
              </div>
              <button
                onClick={() => onAddStopoverBefore(0)}
                className="self-center flex items-center gap-[9px] border border-mono-200 rounded-lg py-2 pr-4 pl-2.5 hover:border-mono-400 hover:bg-mono-50 transition"
              >
                <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                  <Icon name="plus" size={14} className="text-white" />
                </div>
                <span className="text-2sm font-semibold text-orange-500">{t('common.add_stopover')}</span>
                {stopoverCharge && (
                  <span className="text-[15px] font-medium text-mono-600">( {stopoverCharge} )</span>
                )}
              </button>
            </div>

            {/* Before-waypoint stopovers list */}
            {tStopoversBefore.map((stop, i) => (
              <React.Fragment key={stop.id}>
                <div className="flex items-start gap-3 sm:gap-5">
                  <Icon name="place" size={32} className="text-mono-600 shrink-0 mt-2" />
                  <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-2.5 flex flex-col gap-2.5 min-h-16">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-[15px] font-medium text-mono-600 min-w-0 break-keep">
                        {t('common.stopover')}{stopoverCharge ? ` (${stopoverCharge})` : ''}
                      </span>
                      <div className="flex items-center gap-3 shrink-0">
                        <LinkButton size="xs" onClick={() => onEditStopoverBefore(stop.id)}>{t('common.edit')}</LinkButton>
                        <button
                          onClick={() => onRemoveStopoverBefore(stop.id)}
                          className="text-2sm font-semibold text-mono-400 hover:text-mono-600 hover:underline"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </div>
                {stop.address ? (
                      <div className="flex flex-col gap-0.5">
                        {/* BugHerd #82 follow-up: name 굵게, address 보조 */}
                        {stop.name && stop.name !== stop.address ? (
                          <>
                            <span className="text-[16px] font-bold text-mono-900">{stop.name}</span>
                            <span className="text-[15px] font-medium text-mono-600">{stop.address}</span>
                          </>
                        ) : (
                          <span className="text-[16px] font-bold text-mono-900">{stop.address}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-mono-400">{t('common.stopover_search_placeholder')}</span>
                    )}
                  </div>
                </div>

                {/* Add stopover button after this beforeStopover */}
                <div className="flex gap-3 sm:gap-5 py-6">
                  <div className="w-8 flex justify-center shrink-0">
                    <div className="border-l border-dashed border-text-muted" />
                  </div>
                  <button
                    onClick={() => onAddStopoverBefore(i + 1)}
                    className="self-center flex items-center gap-[9px] border border-mono-200 rounded-lg py-2 pr-4 pl-2.5 hover:border-mono-400 hover:bg-mono-50 transition"
                  >
                    <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                      <Icon name="plus" size={14} className="text-white" />
                    </div>
                    <span className="text-2sm font-semibold text-orange-500">{t('common.add_stopover')}</span>
                    {stopoverCharge && (
                      <span className="text-[15px] font-medium text-mono-600">( {stopoverCharge} )</span>
                    )}
                  </button>
                </div>
              </React.Fragment>
            ))}
          </>
        )}

        {/* ---- Fixed waypoints (golf/ski/hospital) ---- */}
        {fixedWaypoints && tFixedWaypoints.map((wp, i) => (
          <React.Fragment key={wp.id || `wp-${i}`}>
            {/* Dashed connector — before-zone이 있을 땐 자체 connector가 이미 있으므로 생략 */}
            {!(showFreeStopoversBefore && i === 0) && (
              <div className="flex gap-3 sm:gap-5 py-3">
                <div className="w-8 flex justify-center shrink-0">
                  <div className="border-l border-dashed border-gray-400 h-6" />
                </div>
              </div>
            )}
            {/* Waypoint card */}
            <div className="flex items-start gap-3 sm:gap-5">
              <Icon name={wp.icon || 'place'} size={32} className="text-mono-600 shrink-0 mt-2" />
              <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-2.5 flex flex-col gap-2.5 min-h-16">
                <div className="flex items-center gap-2.5">
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-[15px] font-medium text-mono-600">{wp.label}</span>
                    <span className="text-[16px] font-bold text-mono-900">{wp.address || t('common.address_search_default')}</span>
                    {wp.detail && (
                      <span className="text-2sm font-medium text-mono-600">{wp.detail}</span>
                    )}
                  </div>
                  {wp.onEdit && (
                    <LinkButton size="xs" onClick={wp.onEdit} className="shrink-0 self-start">{t('common.edit')}</LinkButton>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}

        {/* ---- Free stopover: Add button (before first stopover) ---- */}
        {showFreeStopovers && (
          <div className="flex gap-3 sm:gap-5 py-6">
            <div className="w-8 flex justify-center shrink-0">
              <div className="border-l border-dashed border-text-muted border-gray-400" />
            </div>
            <button
              onClick={() => onAddStopover(0)}
              className="self-center flex items-center gap-[9px] border border-mono-200 rounded-lg py-2 pr-4 pl-2.5 hover:border-mono-400 hover:bg-mono-50 transition"
            >
              <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                <Icon name="plus" size={14} className="text-white" />
              </div>
              <span className="text-2sm font-semibold text-orange-500">{t('common.add_stopover')}</span>
              {stopoverCharge && (
                <span className="text-[15px] font-medium text-mono-600">( {stopoverCharge} )</span>
              )}
            </button>
          </div>
        )}

        {/* ---- Free stopovers list ---- */}
        {showFreeStopovers && tStopovers.map((stop, i) => (
          <React.Fragment key={stop.id}>
            {/* Stopover card */}
            <div className="flex items-start gap-3 sm:gap-5">
              <Icon name="place" size={32} className="text-mono-600 shrink-0 mt-2" />
              <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-2.5 flex flex-col gap-2.5 min-h-16">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[15px] font-medium text-mono-600 min-w-0 break-keep">
                    {t('common.stopover')}{stopoverCharge ? ` (${stopoverCharge})` : ''}
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    <LinkButton size="xs" onClick={() => onEditStopover(stop.id)}>{t('common.edit')}</LinkButton>
                    <button
                      onClick={() => onRemoveStopover(stop.id)}
                      className="text-2sm font-semibold text-mono-400 hover:text-mono-600 hover:underline"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
                {stop.address ? (
                  <div className="flex flex-col gap-0.5">
                    {/* BugHerd #82 follow-up: name 굵게, address 보조 */}
                    {stop.name && stop.name !== stop.address ? (
                      <>
                        <span className="text-[16px] font-bold text-mono-900">{stop.name}</span>
                        <span className="text-[15px] font-medium text-mono-600">{stop.address}</span>
                      </>
                    ) : (
                      <span className="text-[16px] font-bold text-mono-900">{stop.address}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-mono-400">{t('common.stopover_search_placeholder')}</span>
                )}
              </div>
            </div>

            {/* Add stopover button (after this stopover) */}
            <div className="flex gap-3 sm:gap-5 py-6">
              <div className="w-8 flex justify-center shrink-0">
                <div className="border-l border-dashed border-text-muted" />
              </div>
              <button
                onClick={() => onAddStopover(i + 1)}
                className="self-center flex items-center gap-[9px] border border-mono-200 rounded-lg py-2 pr-4 pl-2.5 hover:border-mono-400 hover:bg-mono-50 transition"
              >
                <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                  <Icon name="plus" size={14} className="text-white" />
                </div>
                <span className="text-2sm font-semibold text-orange-500">{t('common.add_stopover')}</span>
                {stopoverCharge && (
                  <span className="text-[15px] font-medium text-mono-600">( {stopoverCharge} )</span>
                )}
              </button>
            </div>
          </React.Fragment>
        ))}

        {/* ---- Simple connector (when no stopovers at all) ---- */}
        {!showFreeStopovers && !fixedWaypoints && (
          <div className="flex gap-3 sm:gap-5 py-3">
            <div className="w-8 flex justify-center shrink-0">
              <div className="border-l border-dashed border-gray-400 h-6" />
            </div>
          </div>
        )}

        {/* 이전에 있던 arrival 직전 h-6 dashed connector는 제거 —
            add-stopover 버튼의 py-6 full dashed가 이미 시각적 연결을 제공하므로 중복 (#hospital-stopover) */}

        {/* ---- Arrival ---- */}
        <div className="flex items-start gap-3 sm:gap-5">
          <Icon name={arrival.icon || 'place'} size={32} className="text-mono-600 shrink-0 mt-2" />
          <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-2.5 flex flex-col gap-2.5 min-h-16">
            <div className="flex items-center gap-2.5">
              <div className="flex-1 flex flex-col gap-1">
                <span className="text-[15px] font-medium text-mono-600">{tArrLabel}</span>
                {/* BugHerd #82 follow-up: name 이 있으면 굵게, address 가 보조. */}
                {tArrName && tArrName !== tArrAddr ? (
                  <>
                    <span className="text-[16px] font-bold text-mono-900">{tArrName}</span>
                    <span className="text-[15px] font-medium text-mono-600">{tArrAddr}</span>
                  </>
                ) : (
                  <span className="text-[16px] font-bold text-mono-900">{tArrAddr}</span>
                )}
              </div>
              <LinkButton size="xs" onClick={onEditArrival} className="shrink-0 self-start">{t('common.edit')}</LinkButton>
            </div>
            {onArrivalDetailChange && (
              <input
                type="text"
                name="arrival-detail"
                autoComplete="off"
                placeholder={t('common.address_detail_placeholder')}
                value={arrivalDetail}
                onChange={onArrivalDetailChange}
                className="w-full px-3 py-2 bg-white border border-mono-100 rounded-lg text-[15px] text-mono-900 placeholder:text-mono-500 focus:outline-none focus:border-orange-500 transition"
              />
            )}
          </div>
        </div>

        {/* ---- 요청사항 (도착지 하단, 도착지 카드와 동일 정렬) ---- */}
        {onNotesChange && (
          <div className="flex gap-3 sm:gap-5 mt-4">
            <div className="w-8 shrink-0" />
            <textarea
              rows={2}
              placeholder={t('common.request_placeholder')}
              value={notes}
              onChange={onNotesChange}
              className="flex-1 px-4 py-3 bg-mono-50 border border-mono-100 rounded-lg text-sm font-normal text-mono-900 placeholder:text-mono-900/50 focus:outline-none focus:border-orange-500 transition resize-none"
            />
          </div>
        )}
      </div>
    </>
  );
}
